/**
 * Exception Activity API service.
 *
 * Thin wrapper over the admin `/exceptions/activity` endpoint that
 * powers the cross-FP triage queue. Response shape mirrors the audit
 * envelope (page/total/total_pages) but uses `items` instead of `logs`
 * because this is a queue, not an audit log.
 */
import { apiClient } from './api'

/**
 * Feature flag for the "Waiting on us" signal (KPI tile + per-row badge).
 *
 * The signal fires when an exception's most recent note was authored
 * by an FP user (i.e. the ball is in the team's court). FP-authored
 * notes don't exist yet (noting is team-only today), so the
 * count is always 0 - we hide the surfaces until the FP note write
 * path ships, then flip this to `true`. The backend still computes
 * `summary.waiting_on_us` so the only change needed is this flag.
 */
export const SHOW_WAITING_ON_US = false

export type ActivityTab = 'blocked' | 'active' | 'ignored' | 'all'

export type ActivityStatus =
  | 'needs_review'
  | 'blocked'
  | 'resolved'
  | 'ignored'

export interface ActivityActor {
  id: number
  name: string | null
  role: string | null
}

export interface ExceptionActivityItem {
  fp_id: number
  fp_code: string
  franchise_name: string
  market: string
  /** Franchise lifecycle status (Onboarding | Active | Terminated | Test). */
  fp_status: string
  country: string
  data_domain: string
  object_id: string
  /**
   * Human-readable reference for the underlying object (sale invoice
   * number, product name, ...). When `reference_is_fallback` is true this
   * is a truncated raw object_id and should render as a muted id.
   */
  reference: string
  reference_is_fallback: boolean
  exception_type_id: number
  exception_code: string
  exception_name: string
  exception_name_short: string | null
  tag_id: number | null
  status: ActivityStatus
  detected_at: string | null
  last_detected_at: string | null
  blocked_at: string | null
  block_reason: string | null
  blocked_by: ActivityActor | null
  resolved_at: string | null
  ignored_at: string | null
  ignored_by: ActivityActor | null
  note_count: number
  latest_note_at: string | null
  latest_note_author: ActivityActor | null
  latest_note_snippet: string | null
  last_activity_at: string
  /** True when the server-side hotness score clears the threshold. Drives
   * the subtle 🔥 "Hot" badge on the Active queue; the raw score is never
   * exposed. */
  is_hot: boolean
}

export interface ExceptionActivitySummary {
  /** Open-and-blocked rows (Blocked queue). */
  blocked: number
  /** Note threads on currently-open rows (Active queue). */
  active: number
  /** Currently-ignored rows (Ignored queue). */
  ignored: number
  /** Oldest open block; UI renders as a relative age. */
  oldest_blocked_at: string | null
  /** Open rows whose last note was authored by an FP user. Inert (0)
   * until FP noting ships; gated in the UI by `SHOW_WAITING_ON_US`. */
  waiting_on_us: number
  last_activity_at: string | null
}

export interface ExceptionActivityListResponse {
  items: ExceptionActivityItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
  summary: ExceptionActivitySummary
}

export interface ExceptionActivityFilters {
  tab?: ActivityTab
  market?: string
  fp_status?: string
  fp_code?: string
  exception_code?: string
  data_domain?: string
  status?: ActivityStatus
  search?: string
  activity_start?: string
  activity_end?: string
  sort_by?:
    | 'last_activity_at'
    | 'blocked_at'
    | 'note_count'
    | 'franchise_name'
    | 'exception_name'
    | 'status'
    | 'hotness'
  sort_dir?: 'asc' | 'desc'
  page?: number
  page_size?: number
}

class ExceptionActivityApi {
  async getList(
    filters: ExceptionActivityFilters = {},
  ): Promise<ExceptionActivityListResponse> {
    const response = await apiClient.get<ExceptionActivityListResponse>(
      '/admin/exceptions/activity',
      { params: filters },
    )
    return response.data
  }
}

export const exceptionActivityApi = new ExceptionActivityApi()
